"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export type CountdownLabels = { days: string; hours: string; minutes: string; done: string };

const MINUTE = 60_000;

/** Whole days, hours and minutes from `now` to `target`; `null` once it has passed. */
export function splitRemaining(target: string, now: number): { days: number; hours: number; minutes: number } | null {
  const left = new Date(target).getTime() - now;
  if (!Number.isFinite(left) || left <= 0) return null;
  return {
    days: Math.floor(left / 86_400_000),
    hours: Math.floor(left / 3_600_000) % 24,
    minutes: Math.floor(left / MINUTE) % 60,
  };
}

/**
 * `SITE-HOME02` part 3: the cohort and contest countdowns, the homepage's one
 * client island below the stage. The first render uses the server's clock
 * (`initialNow`), so the HTML and the hydrated tree agree; then it reads the
 * visitor's clock and moves every 30 seconds. Minutes are its finest unit and
 * nothing slides or flips, so there is no motion to switch off; nothing is
 * announced on its own either (no live region), the time is in the text.
 */
export function Countdown({
  target,
  initialNow,
  locale,
  labels,
  variant,
  className,
}: {
  target: string;
  initialNow: number;
  locale: string;
  labels: CountdownLabels;
  /** `tiles`: three boxes on a dark ground (the spotlight); `inline`: one line ("2d 04h 37m"). */
  variant: "tiles" | "inline";
  className?: string;
}) {
  const [now, setNow] = useState(initialNow);

  useEffect(() => {
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), MINUTE / 2);
    return () => window.clearInterval(id);
  }, []);

  const remaining = splitRemaining(target, now);
  if (!remaining) return <span className={className}>{labels.done}</span>;

  const two = new Intl.NumberFormat(locale === "bn" ? "bn-BD" : "en-US", { minimumIntegerDigits: 2 });
  const one = new Intl.NumberFormat(locale === "bn" ? "bn-BD" : "en-US");
  const parts = [
    { key: "days", value: remaining.days > 99 ? one.format(remaining.days) : two.format(remaining.days), label: labels.days },
    { key: "hours", value: two.format(remaining.hours), label: labels.hours },
    { key: "minutes", value: two.format(remaining.minutes), label: labels.minutes },
  ];

  if (variant === "inline") {
    return (
      <time dateTime={target} className={cn("tabular-nums", className)}>
        {parts.map((part, index) => (
          <span key={part.key}>
            {index > 0 ? " " : null}
            {part.value}
            <span className="ml-0.5 text-[0.6em] font-semibold opacity-70">{part.label}</span>
          </span>
        ))}
      </time>
    );
  }

  return (
    <time dateTime={target} className={cn("grid grid-cols-3 gap-2.5", className)}>
      {parts.map((part) => (
        <span
          key={part.key}
          className="flex flex-col items-center gap-1 rounded-xl border border-white/10 bg-white/[0.06] py-3"
        >
          <span className="font-mono text-3xl font-bold leading-none tabular-nums text-white sm:text-4xl">{part.value}</span>
          <span className="text-xs text-neutral-400">{part.label}</span>
        </span>
      ))}
    </time>
  );
}
